import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import {
  PageWrapper,
  HeroSection,
  Container,
  PageTitle,
  PageSubtitle,
  PremiumCard,
  PrimaryButton,
  SecondaryButton,
  FormGroup,
  Label,
  Input,
  Select,
  InfoBox
} from '../styles/PremiumStyles';
import { theme } from '../styles/theme';

// ── Types ──────────────────────────────────────────────────────────

interface TransferRow {
  id: string;
  tenPercentPFAmount: string;
  fullTransferAccumulatedValue: string;
  fullTransferSurrenderCharge: string;
  current2yrParRate: string;
  fee2yrPar: string;
  spread2yrPar: string;
  current1yrParRate: string;
  fee1yrPar: string;
  spread1yrPar: string;
  current2yrCapRate: string;
  fee2yrCap: string;
  spread2yrCap: string;
  current1yrCapRate: string;
  fee1yrCap: string;
  spread1yrCap: string;
  totalGrossAmtToTransfer: string;
}

interface ProductRow {
  id: string;
  productToTransferTo: string;
  clientName: string;
  age: string;
  state: string;
  transfers: TransferRow[];
}

interface ContactInfo {
  name: string;
  email: string;
  phone: string;
}

// ── Constants ──────────────────────────────────────────────────────

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

const createEmptyTransfer = (): TransferRow => ({
  id: generateId(),
  tenPercentPFAmount: '',
  fullTransferAccumulatedValue: '',
  fullTransferSurrenderCharge: '',
  current2yrParRate: '',
  fee2yrPar: '',
  spread2yrPar: '',
  current1yrParRate: '',
  fee1yrPar: '',
  spread1yrPar: '',
  current2yrCapRate: '',
  fee2yrCap: '',
  spread2yrCap: '',
  current1yrCapRate: '',
  fee1yrCap: '',
  spread1yrCap: '',
  totalGrossAmtToTransfer: '',
});

const createEmptyProduct = (): ProductRow => ({
  id: generateId(),
  productToTransferTo: '',
  clientName: '',
  age: '',
  state: '',
  transfers: [createEmptyTransfer()],
});

const createTestTransfer = (): TransferRow => ({
  id: generateId(),
  tenPercentPFAmount: '25000',
  fullTransferAccumulatedValue: '250000',
  fullTransferSurrenderCharge: '5000',
  current2yrParRate: '150',
  fee2yrPar: '1.25',
  spread2yrPar: '0.50',
  current1yrParRate: '120',
  fee1yrPar: '1.00',
  spread1yrPar: '0.25',
  current2yrCapRate: '12.5',
  fee2yrCap: '1.50',
  spread2yrCap: '0.75',
  current1yrCapRate: '10.0',
  fee1yrCap: '1.25',
  spread1yrCap: '0.50',
  totalGrossAmtToTransfer: '275000',
});

const createTestProduct = (): ProductRow => ({
  id: generateId(),
  productToTransferTo: 'HV10 Fixed 1st yr',
  clientName: 'John Smith',
  age: '65',
  state: 'TX',
  transfers: [createTestTransfer()],
});

// ── Styled Components ──────────────────────────────────────────────

const EnhancedInput = styled(Input)`
  font-size: 16px;
  padding: 16px;
  min-height: 52px;
  border: 2px solid #e0e0e0;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: 15px;
    min-height: 50px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 14px;
    min-height: 48px;
  }

  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
`;

const EnhancedSelect = styled(Select)`
  font-size: 16px;
  padding: 16px;
  min-height: 52px;
  border: 2px solid #e0e0e0;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: 15px;
    min-height: 50px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 14px;
    min-height: 48px;
  }

  &:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
`;

const EnhancedLabel = styled(Label)`
  font-size: 15px;
  font-weight: 600;
  color: #1a2332;
  margin-bottom: 8px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
    margin-bottom: 6px;
  }
`;

const MainCard = styled(PremiumCard)`
  max-width: 1100px;
  margin: -50px auto 50px;
  background: white;

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: -30px 15px 30px;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 2px solid rgba(255, 215, 0, 0.3);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 20px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 25px;
    border-radius: 15px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px;
    border-radius: 12px;
  }

  h3 {
    font-size: 24px;
    color: #1a2332;
    margin-bottom: 25px;
    font-weight: 700;
    text-align: center;

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 20px;
      margin-bottom: 20px;
    }
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ProductCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 2px solid rgba(26, 35, 50, 0.15);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 20px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 25px;
    border-radius: 15px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid rgba(255, 215, 0, 0.2);

  h3 {
    font-size: 20px;
    color: #1a2332;
    font-weight: 700;
    margin: 0;

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 18px;
    }
  }
`;

const TransferCard = styled.div`
  background: rgba(248, 249, 250, 0.7);
  border: 1px solid rgba(26, 35, 50, 0.1);
  border-radius: 14px;
  padding: 22px;
  margin-bottom: 15px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 18px;
    border-radius: 12px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 15px;
    border-radius: 10px;
  }
`;

const TransferHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(26, 35, 50, 0.08);

  h4 {
    font-size: 16px;
    color: #1a2332;
    font-weight: 700;
    margin: 0;

    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 15px;
    }
  }
`;

const RemoveButton = styled.button`
  background: rgba(255, 68, 68, 0.1);
  color: #ff4444;
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 68, 68, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      background: rgba(255, 68, 68, 0.1);
    }
  }
`;

const SubSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #1a2332;
  margin: 25px 0 15px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  letter-spacing: 0.5px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 15px;
    margin: 20px 0 12px 0;
  }
`;

const TransferSubSectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 700;
  color: #1a2332;
  margin: 18px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8e8e8;
  letter-spacing: 0.3px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 13px;
    margin: 14px 0 10px 0;
  }
`;

const BasicInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ThreeColGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 10px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const CompactFormGroup = styled(FormGroup)`
  margin-bottom: 10px;
`;

const CompactLabel = styled(EnhancedLabel)`
  font-size: 13px;
  margin-bottom: 5px;
  text-transform: none;
  letter-spacing: 0;
`;

const CompactInput = styled(EnhancedInput)`
  padding: 12px;
  min-height: 44px;
  font-size: 15px;
`;

const CompactSelect = styled(EnhancedSelect)`
  padding: 12px;
  min-height: 44px;
  font-size: 15px;
`;

const AddTransferButton = styled(SecondaryButton)`
  width: 100%;
  padding: 12px;
  font-size: 14px;
  margin-top: 5px;
`;

const BottomButtonRow = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 40px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const AddProductButton = styled(SecondaryButton)`
  flex: 1;
  padding: 16px;
  font-size: 16px;
`;

const EnhancedPrimaryButton = styled(PrimaryButton)`
  flex: 1;
  font-size: 18px;
  padding: 18px 32px;
  min-height: 56px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 17px;
    padding: 16px 28px;
    min-height: 54px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 16px;
    padding: 16px 24px;
    min-height: 52px;
    width: 100%;
  }
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  border: 2px solid #4caf50;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-top: 20px;

  h3 {
    color: #2e7d32;
    font-size: 26px;
    margin-bottom: 18px;
    font-weight: 700;
    line-height: 1.3;
  }

  p {
    color: #2e7d32;
    font-size: 17px;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 30px;
    h3 { font-size: 24px; }
    p { font-size: 16px; }
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 25px 20px;
    h3 { font-size: 22px; }
    p { font-size: 15px; }
  }
`;

const SectionHeading = styled.h3`
  font-size: 22px;
  color: #1a2332;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 19px;
    margin-bottom: 15px;
  }
`;

// ── Component ──────────────────────────────────────────────────────

export const PermutationRequest: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isTestMode = searchParams.has('test');

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: '',
  });

  const [products, setProducts] = useState<ProductRow[]>([createEmptyProduct()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Anti-spam
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  // ── Handlers ──

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({ ...prev, [name]: value }));
  };

  const updateProduct = (id: string, field: keyof Omit<ProductRow, 'id' | 'transfers'>, value: string) => {
    setProducts(prev =>
      prev.map(p => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const updateTransfer = (productId: string, transferId: string, field: keyof Omit<TransferRow, 'id'>, value: string) => {
    setProducts(prev =>
      prev.map(p =>
        p.id === productId
          ? {
              ...p,
              transfers: p.transfers.map(t =>
                t.id === transferId ? { ...t, [field]: value } : t
              ),
            }
          : p
      )
    );
  };

  const addProduct = () => {
    if (isTestMode) {
      setProducts(prev => [...prev, createTestProduct()]);
    } else {
      const first = products[0];
      const newProduct = createEmptyProduct();
      newProduct.clientName = first?.clientName || '';
      newProduct.age = first?.age || '';
      newProduct.state = first?.state || '';
      setProducts(prev => [...prev, newProduct]);
    }
  };

  const removeProduct = (id: string) => {
    if (products.length > 1) {
      setProducts(prev => prev.filter(p => p.id !== id));
    }
  };

  const addTransfer = (productId: string) => {
    const newTransfer = isTestMode ? createTestTransfer() : createEmptyTransfer();
    setProducts(prev =>
      prev.map(p =>
        p.id === productId
          ? { ...p, transfers: [...p.transfers, newTransfer] }
          : p
      )
    );
  };

  const removeTransfer = (productId: string, transferId: string) => {
    setProducts(prev =>
      prev.map(p => {
        if (p.id !== productId || p.transfers.length <= 1) return p;
        return { ...p, transfers: p.transfers.filter(t => t.id !== transferId) };
      })
    );
  };

  const fillTestData = () => {
    setContactInfo({
      name: 'Test User',
      email: 'test@example.com',
      phone: '(555) 123-4567',
    });
    setProducts([createTestProduct()]);
  };

  const resetForm = () => {
    setSubmitSuccess(false);
    setContactInfo({ name: '', email: '', phone: '' });
    setProducts([createEmptyProduct()]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactInfo.name || !contactInfo.email) {
      alert('Please fill in your name and email address.');
      return;
    }

    // Anti-spam
    if (honeypot) {
      console.warn('Spam detected: honeypot field filled');
      return;
    }
    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        from_name: contactInfo.name,
        from_email: contactInfo.email,
        phone: contactInfo.phone || 'Not provided',
        submission_type: 'permutation_request',
        formLoadTime: formLoadTime,
        website: honeypot,
        products: products.map(({ id, transfers, ...rest }) => ({
          ...rest,
          transfers: transfers.map(({ id: _tid, ...tRest }) => tRest),
        })),
        message: `Permutation Request from ${contactInfo.name} with ${products.length} product(s).`,
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: emailData }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ──

  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <HeroSection style={{ color: 'white' }}>
          <Container>
            <PageTitle style={{ color: 'white' }}>
              Permutation Request
            </PageTitle>
            <PageSubtitle style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
              Submit your product details and our team will return results within 2 business days
            </PageSubtitle>
          </Container>
        </HeroSection>

        <MainCard>
          {!submitSuccess ? (
            <form onSubmit={handleSubmit}>
              <InfoBox $variant="info" style={{ marginBottom: '30px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1a2332' }}>How It Works</h4>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Fill in your contact information and add one or more products below.
                  Each product can have multiple transfer scenarios to test.
                  Not every field is required -- provide what you have available.
                  Our team will process your request and return results within 2 business days.
                </p>
              </InfoBox>

              {isTestMode && (
                <div style={{ textAlign: 'right', marginBottom: '10px' }}>
                  <button
                    type="button"
                    onClick={fillTestData}
                    style={{
                      background: 'none',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      color: '#999',
                      cursor: 'pointer',
                    }}
                  >
                    Fill test data
                  </button>
                </div>
              )}

              {/* ── Contact Section ── */}
              <ContactCard>
                <h3>Contact Information</h3>
                <ContactGrid>
                  <FormGroup>
                    <EnhancedLabel>Name *</EnhancedLabel>
                    <EnhancedInput
                      type="text"
                      name="name"
                      value={contactInfo.name}
                      onChange={handleContactChange}
                      placeholder="Your full name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <EnhancedLabel>Email *</EnhancedLabel>
                    <EnhancedInput
                      type="email"
                      name="email"
                      value={contactInfo.email}
                      onChange={handleContactChange}
                      placeholder="your@email.com"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <EnhancedLabel>Phone</EnhancedLabel>
                    <EnhancedInput
                      type="tel"
                      name="phone"
                      value={contactInfo.phone}
                      onChange={handleContactChange}
                      placeholder="(555) 555-5555"
                    />
                  </FormGroup>
                </ContactGrid>
              </ContactCard>

              {/* ── Product Rows ── */}
              <SectionHeading style={{ marginTop: '35px' }}>Product Details</SectionHeading>

              {products.map((product, pIndex) => (
                <ProductCard key={product.id}>
                  <ProductHeader>
                    <h3>Product #{pIndex + 1}</h3>
                    {products.length > 1 && (
                      <RemoveButton type="button" onClick={() => removeProduct(product.id)}>
                        Remove
                      </RemoveButton>
                    )}
                  </ProductHeader>

                  {/* Basic Info */}
                  <SubSectionTitle>Basic Information</SubSectionTitle>
                  <BasicInfoGrid>
                    <CompactFormGroup>
                      <CompactLabel>Product (rate) to transfer to</CompactLabel>
                      <CompactInput
                        value={product.productToTransferTo}
                        onChange={e => updateProduct(product.id, 'productToTransferTo', e.target.value)}
                        placeholder="e.g. HV10 Fixed 1st yr"
                      />
                    </CompactFormGroup>
                    <CompactFormGroup>
                      <CompactLabel>Client Name</CompactLabel>
                      <CompactInput
                        value={product.clientName}
                        onChange={e => updateProduct(product.id, 'clientName', e.target.value)}
                      />
                    </CompactFormGroup>
                    <CompactFormGroup>
                      <CompactLabel>Age</CompactLabel>
                      <CompactInput
                        type="number"
                        value={product.age}
                        onChange={e => updateProduct(product.id, 'age', e.target.value)}
                      />
                    </CompactFormGroup>
                    <CompactFormGroup>
                      <CompactLabel>State</CompactLabel>
                      <CompactSelect
                        value={product.state}
                        onChange={e => updateProduct(product.id, 'state', e.target.value)}
                      >
                        <option value="">Select...</option>
                        {US_STATES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </CompactSelect>
                    </CompactFormGroup>
                  </BasicInfoGrid>

                  {/* Transfers */}
                  {product.transfers.map((transfer, tIndex) => (
                    <TransferCard key={transfer.id}>
                      <TransferHeader>
                        <h4>Transfer #{tIndex + 1}</h4>
                        <RemoveButton
                          type="button"
                          onClick={() => removeTransfer(product.id, transfer.id)}
                          disabled={product.transfers.length <= 1}
                        >
                          Remove
                        </RemoveButton>
                      </TransferHeader>

                      {/* Transfer Amounts */}
                      <TransferSubSectionTitle>Transfer Amounts</TransferSubSectionTitle>
                      <ThreeColGrid>
                        <CompactFormGroup>
                          <CompactLabel>10% PF Amt to Transfer</CompactLabel>
                          <CompactInput
                            value={transfer.tenPercentPFAmount}
                            onChange={e => updateTransfer(product.id, transfer.id, 'tenPercentPFAmount', e.target.value)}
                            placeholder="$"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Full Transfer Accumulated Value</CompactLabel>
                          <CompactInput
                            value={transfer.fullTransferAccumulatedValue}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fullTransferAccumulatedValue', e.target.value)}
                            placeholder="$"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Full Transfer Surrender Charge ($)</CompactLabel>
                          <CompactInput
                            value={transfer.fullTransferSurrenderCharge}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fullTransferSurrenderCharge', e.target.value)}
                            placeholder="$"
                          />
                        </CompactFormGroup>
                      </ThreeColGrid>

                      {/* Participation Rate Details */}
                      <TransferSubSectionTitle>Participation Rate Details</TransferSubSectionTitle>
                      <ThreeColGrid>
                        <CompactFormGroup>
                          <CompactLabel>Current 2yr Par Rate %</CompactLabel>
                          <CompactInput
                            value={transfer.current2yrParRate}
                            onChange={e => updateTransfer(product.id, transfer.id, 'current2yrParRate', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Fee % /yr (2yr Par)</CompactLabel>
                          <CompactInput
                            value={transfer.fee2yrPar}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fee2yrPar', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Spread % /yr (2yr Par)</CompactLabel>
                          <CompactInput
                            value={transfer.spread2yrPar}
                            onChange={e => updateTransfer(product.id, transfer.id, 'spread2yrPar', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                      </ThreeColGrid>
                      <ThreeColGrid>
                        <CompactFormGroup>
                          <CompactLabel>Current 1yr Par Rate %</CompactLabel>
                          <CompactInput
                            value={transfer.current1yrParRate}
                            onChange={e => updateTransfer(product.id, transfer.id, 'current1yrParRate', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Fee % /yr (1yr Par)</CompactLabel>
                          <CompactInput
                            value={transfer.fee1yrPar}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fee1yrPar', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Spread % /yr (1yr Par)</CompactLabel>
                          <CompactInput
                            value={transfer.spread1yrPar}
                            onChange={e => updateTransfer(product.id, transfer.id, 'spread1yrPar', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                      </ThreeColGrid>

                      {/* Cap Rate Details */}
                      <TransferSubSectionTitle>Cap Rate Details</TransferSubSectionTitle>
                      <ThreeColGrid>
                        <CompactFormGroup>
                          <CompactLabel>Current 2yr Cap Rate %</CompactLabel>
                          <CompactInput
                            value={transfer.current2yrCapRate}
                            onChange={e => updateTransfer(product.id, transfer.id, 'current2yrCapRate', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Fee % /yr (2yr Cap)</CompactLabel>
                          <CompactInput
                            value={transfer.fee2yrCap}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fee2yrCap', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Spread % /yr (2yr Cap)</CompactLabel>
                          <CompactInput
                            value={transfer.spread2yrCap}
                            onChange={e => updateTransfer(product.id, transfer.id, 'spread2yrCap', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                      </ThreeColGrid>
                      <ThreeColGrid>
                        <CompactFormGroup>
                          <CompactLabel>Current 1yr Cap Rate %</CompactLabel>
                          <CompactInput
                            value={transfer.current1yrCapRate}
                            onChange={e => updateTransfer(product.id, transfer.id, 'current1yrCapRate', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Fee % /yr (1yr Cap)</CompactLabel>
                          <CompactInput
                            value={transfer.fee1yrCap}
                            onChange={e => updateTransfer(product.id, transfer.id, 'fee1yrCap', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                        <CompactFormGroup>
                          <CompactLabel>Spread % /yr (1yr Cap)</CompactLabel>
                          <CompactInput
                            value={transfer.spread1yrCap}
                            onChange={e => updateTransfer(product.id, transfer.id, 'spread1yrCap', e.target.value)}
                            placeholder="%"
                          />
                        </CompactFormGroup>
                      </ThreeColGrid>

                      {/* Total */}
                      <TransferSubSectionTitle>Total</TransferSubSectionTitle>
                      <CompactFormGroup>
                        <CompactLabel>Total Gross Amt to Transfer from All Accts</CompactLabel>
                        <CompactInput
                          value={transfer.totalGrossAmtToTransfer}
                          onChange={e => updateTransfer(product.id, transfer.id, 'totalGrossAmtToTransfer', e.target.value)}
                          placeholder="$"
                        />
                      </CompactFormGroup>
                    </TransferCard>
                  ))}

                  <AddTransferButton type="button" onClick={() => addTransfer(product.id)}>
                    + Add Additional Transfer
                  </AddTransferButton>
                </ProductCard>
              ))}

              {/* Honeypot (anti-spam) */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Bottom Buttons */}
              <BottomButtonRow>
                <AddProductButton type="button" onClick={addProduct}>
                  + Add Another Product
                </AddProductButton>
                <EnhancedPrimaryButton type="submit" disabled={isSubmitting || !contactInfo.name || !contactInfo.email}>
                  {isSubmitting ? 'Submitting...' : 'Submit Permutation Request'}
                </EnhancedPrimaryButton>
              </BottomButtonRow>
            </form>
          ) : (
            <SuccessMessage>
              <h3>Permutation Request Submitted!</h3>
              <p>
                Thank you for your submission. Our team will process your request and return
                results within 2 business days to {contactInfo.email}.
              </p>
              <div style={{ marginTop: '25px' }}>
                <SecondaryButton onClick={resetForm}>
                  Submit Another Request
                </SecondaryButton>
              </div>
            </SuccessMessage>
          )}
        </MainCard>
      </PageWrapper>
    </SharedLayoutUltimate>
  );
};
